import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NetworkStatus } from '@apollo/client';

import { ToastTypes, GlobalToastTypeNames } from 'types';

import {
  useRunPandaDocSyncMutation,
  useRunPandaDocReparseMutation,
  usePandaDocSyncStatusQuery,
} from 'core/graphql';

import { useToasts } from 'core/hooks';

/** Status poll cadence while a sync is running (ТЗ §3.3: ~15-30 s). */
const POLL_INTERVAL_MS = 20000;
/** Soft cap — past this we stop polling and ask the user to reload (ТЗ §3.3). */
const SYNC_TIMEOUT_MS = 30 * 60 * 1000;

interface IPandadocSyncHook {
  /** Queues a full PandaDoc re-sync and switches the UI to progress mode. */
  triggerSync: () => void;
  /**
   * Queues the repair pass: re-parse signer contact data on the whole cached
   * inventory with the current parser, then drain whatever needs fresh
   * details. Admin-only on the backend. Shares this hook's progress polling.
   */
  triggerReparse: () => void;
  /** True while a sync/reparse is queued/running and progress is polled. */
  syncing: boolean;
  /** True when the run in progress is the reparse (for the button label). */
  reparsing: boolean;
  /** Documents still awaiting details; `null` until the first poll lands. */
  detailsPending: number | null;
}

/**
 * Orchestrates the manual «Обновить данные» button: fires `triggerPandadocSync`,
 * shows the backend toast message, then polls `pandadocSyncStatus` until the
 * details backlog drains (`detailsPending === 0`), a sync error appears, or the
 * soft timeout is hit. `onSynced` runs once on successful completion so the
 * caller can re-query its data (the provisioning folders list).
 */
export const usePandadocSync = (onSynced: () => void): IPandadocSyncHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const [syncing, setSyncing] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [detailsPending, setDetailsPending] = useState<number | null>(null);

  const startedAtRef = useRef(0);
  // Status responses seen since the trigger — the first one is the snapshot
  // taken right after enqueue and may still read 0 before the backend has
  // marked documents for backfill, so completion is judged from the second on.
  const responsesRef = useRef(0);
  const prevNetworkStatusRef = useRef<NetworkStatus>();
  // Kept in a ref so the polling effect needn't list `onSynced` as a dep.
  const onSyncedRef = useRef(onSynced);
  onSyncedRef.current = onSynced;

  const [runSync] = useRunPandaDocSyncMutation();
  const [runReparse] = useRunPandaDocReparseMutation();

  const { data, networkStatus } = usePandaDocSyncStatusQuery({
    skip: !syncing,
    pollInterval: syncing ? POLL_INTERVAL_MS : 0,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const notify = useCallback(
    (type: ToastTypes, title: string) => {
      addToast({
        data: { type, title },
        type: GlobalToastTypeNames.PandadocSync,
      });
    },
    [addToast],
  );

  const stopPolling = useCallback(() => {
    setSyncing(false);
    setReparsing(false);
    setDetailsPending(null);
    responsesRef.current = 0;
    prevNetworkStatusRef.current = undefined;
  }, []);

  const triggerSync = useCallback(() => {
    if (syncing) {
      return;
    }
    startedAtRef.current = Date.now();
    responsesRef.current = 0;
    prevNetworkStatusRef.current = undefined;

    runSync()
      .then((result) => {
        const payload = result.data?.triggerPandadocSync;
        if (payload?.message) {
          notify(ToastTypes.info, payload.message);
        }
        // Enter progress mode regardless of `enqueued`: the sync was either
        // just queued or is already running — both need polling (ТЗ §3.2).
        setSyncing(true);
      })
      .catch((error: Error) => {
        notify(
          ToastTypes.error,
          t('pages.pandadocProvisioning.toasts.syncError', {
            error: error.message,
          }),
        );
      });
  }, [syncing, runSync, notify, t]);

  const triggerReparse = useCallback(() => {
    if (syncing) {
      return;
    }
    startedAtRef.current = Date.now();
    responsesRef.current = 0;
    prevNetworkStatusRef.current = undefined;

    runReparse({ variables: { markRefetch: true } })
      .then((result) => {
        const payload = result.data?.reparsePandadocRecipients;
        if (payload?.message) {
          notify(ToastTypes.info, payload.message);
        }
        // `enqueued: false` means a run already holds the lock — poll either
        // way, both cases end with the backlog drained (mirrors triggerSync).
        setSyncing(true);
        setReparsing(true);
      })
      .catch((error: Error) => {
        notify(
          ToastTypes.error,
          t('pages.pandadocProvisioning.toasts.reparseError', {
            error: error.message,
          }),
        );
      });
  }, [syncing, runReparse, notify, t]);

  useEffect(() => {
    if (!syncing) {
      return;
    }

    // Act once per completed fetch — only on the transition into `ready`.
    const justReady =
      networkStatus === NetworkStatus.ready &&
      prevNetworkStatusRef.current !== NetworkStatus.ready;
    prevNetworkStatusRef.current = networkStatus;
    if (!justReady) {
      return;
    }

    const status = data?.pandadocSyncStatus;
    if (!status) {
      return;
    }
    responsesRef.current += 1;

    if (Date.now() - startedAtRef.current > SYNC_TIMEOUT_MS) {
      notify(
        ToastTypes.warning,
        t('pages.pandadocProvisioning.toasts.syncTimeout'),
      );
      stopPolling();
      return;
    }

    if (status.lastError) {
      notify(
        ToastTypes.error,
        t('pages.pandadocProvisioning.toasts.syncError', {
          error: status.lastError,
        }),
      );
      stopPolling();
      return;
    }

    const pending = status.detailsPending ?? 0;
    setDetailsPending(pending);

    // `detailsPending === 0` alone is not "done": right after the trigger the
    // backend hasn't marked anything for backfill yet, and the reparse scans
    // the whole inventory before it does. `syncRunning` (the backend's own
    // run lock) is the authority — while it is held, a run is genuinely in
    // flight. The `responses > 1` guard still covers the gap between the
    // mutation returning and the worker picking the task up.
    if (pending === 0 && !status.syncRunning && responsesRef.current > 1) {
      notify(
        ToastTypes.success,
        reparsing
          ? t('pages.pandadocProvisioning.toasts.reparseDone')
          : t('pages.pandadocProvisioning.toasts.syncDone'),
      );
      stopPolling();
      onSyncedRef.current();
    }
  }, [data, networkStatus, syncing, reparsing, notify, stopPolling, t]);

  return { triggerSync, triggerReparse, syncing, reparsing, detailsPending };
};
